import { type FC, type PropsWithChildren } from 'react';
import { Controller, type Control, useFieldArray } from 'react-hook-form';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Autocomplete,
  Box,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { MeterTypeLabel } from '#apps/client-web/features/audit-form-background/constants';
import { type EditAuditFormBackgroundData } from '#apps/client-web/pages/audit-form-background/types';
import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';
import { FileInput } from '#apps/client-web/features/file-storage/components/file-input/file-input';

import {
  SubFormDialog,
  type SubFormDialogProps,
} from '#packages/web/layouts/dialog/templates/sub-form';
import { Fab } from '#packages/web/foundations/buttons/fab';
import { DeleteConfirmationDialog } from '#packages/web/layouts/dialog/templates/delete-confirmation';
import { useDialog } from '#packages/web/hooks/use-dialog';
import { MessagePage } from '#packages/web/layouts/page/templates/message-page';
import { Message } from '#packages/web/foundations/feedback/message';
import { type Meter } from '#packages/data-collection/models/audit-background-form/categories/general';

export type MetersFormDialogProps = Pick<
  SubFormDialogProps,
  'onClose' | 'open'
> & {
  control: Control<EditAuditFormBackgroundData>;
};

export const MetersFormDialog: FC<PropsWithChildren<MetersFormDialogProps>> = ({
  open,

  control,

  onClose,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'general.meters',
  });

  const deleteMeterDialog = useDialog<number>();
  const deleteAttachmentDialog = useDialog<{ proceed: () => void }>();

  return (
    <SubFormDialog title="Meters" open={open} onClose={onClose}>
      <Stack spacing={3} sx={{ pb: 10 }}>
        {fields.length === 0 && (
          <MessagePage
            message={<Message>No meters added yet. Tap + to add one.</Message>}
          />
        )}
        {fields.map((field, index) => (
          <Box key={field.id}>
            <Stack spacing={2}>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">
                  Meter {index + 1}
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  aria-label="remove meter"
                  onClick={() => deleteMeterDialog.open(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Controller
                    control={control}
                    name={`general.meters.${index}.type`}
                    rules={{
                      required: {
                        value: true,
                        message: 'Meter type is required',
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <FormControl
                        fullWidth
                        {...getFormErrorAttributes(fieldState.error)}>
                        <Autocomplete
                          {...field}
                          {...getFormErrorAttributes(fieldState.error)}
                          getOptionLabel={(option) =>
                            MeterTypeLabel[option as Meter['type']]
                          }
                          options={Object.keys(MeterTypeLabel)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Type"
                              variant="outlined"
                              {...getFormErrorAttributes(fieldState.error)}
                            />
                          )}
                          value={field.value ?? null}
                          onChange={(_, value) => field.onChange(value)}
                        />
                        <FormHelperText>
                          {fieldState.error?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    control={control}
                    name={`general.meters.${index}.location`}
                    rules={{ maxLength: 120 }}
                    render={({ field, fieldState }) => (
                      <FormControl
                        fullWidth
                        {...getFormErrorAttributes(fieldState.error)}>
                        <TextField
                          {...field}
                          label="Location"
                          type="text"
                          variant="outlined"
                          {...getFormErrorAttributes(fieldState.error)}
                        />
                        <FormHelperText>
                          {fieldState.error?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    control={control}
                    name={`general.meters.${index}.serialNumber`}
                    rules={{ maxLength: 120 }}
                    render={({ field, fieldState }) => (
                      <FormControl
                        fullWidth
                        {...getFormErrorAttributes(fieldState.error)}>
                        <TextField
                          {...field}
                          label="Serial Number"
                          type="text"
                          variant="outlined"
                          {...getFormErrorAttributes(fieldState.error)}
                        />
                        <FormHelperText>
                          {fieldState.error?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Controller
                    control={control}
                    name={`general.meters.${index}.description`}
                    rules={{ maxLength: 500 }}
                    render={({ field, fieldState }) => (
                      <FormControl
                        fullWidth
                        {...getFormErrorAttributes(fieldState.error)}>
                        <TextField
                          {...field}
                          label="Description"
                          type="text"
                          variant="outlined"
                          multiline
                          minRows={2}
                          {...getFormErrorAttributes(fieldState.error)}
                        />
                        <FormHelperText>
                          {fieldState.error?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Controller
                    control={control}
                    name={`general.meters.${index}.attachments`}
                    render={({ field, fieldState }) => (
                      <FormControl
                        fullWidth
                        {...getFormErrorAttributes(fieldState.error)}>
                        <FileInput
                          {...field}
                          label="Attachments"
                          onRemoveRequest={(_, proceed) =>
                            deleteAttachmentDialog.open({ proceed })
                          }
                          {...getFormErrorAttributes(fieldState.error)}
                        />
                        <FormHelperText>
                          {fieldState.error?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </Stack>
            {index < fields.length - 1 && <Divider sx={{ mt: 3 }} />}
          </Box>
        ))}
      </Stack>

      <Fab
        tooltip="Add meter"
        onClick={() =>
          append({
            location: '',
            serialNumber: '',
            description: '',
            type: 'electricity',
          })
        }
      />

      <DeleteConfirmationDialog
        open={deleteMeterDialog.isOpen}
        dialogTitle="Remove Meter"
        confirmButtonLabel="Remove"
        onConfirm={() => {
          remove(deleteMeterDialog.data!);
          deleteMeterDialog.close();
        }}
        onCancel={() => deleteMeterDialog.close()}
      />

      <DeleteConfirmationDialog
        open={deleteAttachmentDialog.isOpen}
        dialogTitle="Remove Attachment"
        confirmButtonLabel="Remove"
        onConfirm={() => {
          deleteAttachmentDialog.data?.proceed();
          deleteAttachmentDialog.close();
        }}
        onCancel={() => deleteAttachmentDialog.close()}
      />
    </SubFormDialog>
  );
};
